import React, { useState, useEffect, useCallback } from 'react';
import TabCollecte from '../tabs/TabCollecte';
import { apiGet, apiPost, apiPut } from '../utils/api-client';

/**
 * TabCollecteContainer — Sprint 1 + fix Sprint 2 (URL alignment) + Funnel V2 (feedbacks privés)
 *
 * Mapping URL frontend → endpoint backend :
 *  - POST /api/reviews/collect              → envoi SMS via Brevo (avec quota)
 *  - GET  /api/dashboard/sms-log            → historique des envois (50 derniers)
 *  - GET  /api/dashboard/sms-usage          → quota mensuel { quota, used, unlimited, ... }
 *  - GET  /api/dashboard/google-reviews     → 5 derniers avis pour panel droit
 *  - GET  /api/dashboard/feedbacks          → feedbacks privés du funnel V2 (texte + RPS + satisfaction)
 *  - GET  /api/restaurants/config           → délai d'envoi SMS persisté (Sprint 1 t2)
 *  - PUT  /api/restaurants/config           → save délai d'envoi SMS (Sprint 1 t2)
 *
 * Convention : si le backend renvoie `unlimited: true` (plan intensif), on
 * fixe `total = null` côté state. TabCollecte affiche alors "X SMS · plan
 * illimité" au lieu d'une barre de pourcentage qui n'a pas de sens.
 *
 * À garder à jour si on renomme/déplace ces endpoints (cf bug Sprint 2 où
 * /api/sms-log et /api/restaurants/sms-quota retournaient 404).
 */
export default function TabCollecteContainer() {
  const [contacts, setContacts]           = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  // total = null → plan illimité (frontend affiche "illimité" au lieu d'une barre %)
  const [smsQuota, setSmsQuota]           = useState({ used: 0, total: 200 });
  // Sprint 1 — ticket 2 : config persistée du délai d'envoi SMS
  // (séparée de l'horizon objectif du wizard Configuration).
  const [delaiEnvoiMinutes, setDelaiEnvoiMinutes] = useState(60);
  // Funnel V2 — feedbacks privés capturés via la Page 2 du funnel client.
  // Format backend (cf. dashboard.js GET /feedbacks) :
  //   { id, texte, telephone_client, traite, vue, created_at, prenom, satisfaction, rps_score }
  // rps_score peut être NULL (anciennes lignes funnel V1 ou client n'a pas touché le slider).
  const [feedbacksPrives, setFeedbacksPrives] = useState([]);
  // Taux de complétion (= avis générés / scans) — recadre la métrique SMS devenue
  // secondaire depuis le funnel V3. Même calcul que la page QR (TabQRCodeContainer).
  const [completion, setCompletion] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [historyRes, quotaRes, reviewsRes, configRes, feedbacksRes, dashRes] = await Promise.all([
        apiGet('/api/dashboard/sms-log').catch(() => null),
        apiGet('/api/dashboard/sms-usage').catch(() => null),
        apiGet('/api/dashboard/google-reviews?limit=5').catch(() => null),
        apiGet('/api/restaurants/config').catch(() => null),
        apiGet('/api/dashboard/feedbacks').catch(() => null),
        apiGet('/api/dashboard/').catch(() => null),
      ]);
      const history = historyRes?.logs || historyRes?.data || historyRes || [];
      setContacts(Array.isArray(history) ? history : []);

      // Backend retourne {quota, used, unlimited} (cf dashboard.js:467 sms-usage).
      // unlimited === true → total = null (plan intensif). Sinon total = quota ?? 200.
      if (quotaRes && (quotaRes.used != null || quotaRes.quota != null || quotaRes.unlimited)) {
        setSmsQuota({
          used: quotaRes.used || 0,
          total: quotaRes.unlimited ? null : (quotaRes.quota ?? 200),
        });
      }

      const reviews = reviewsRes?.reviews || reviewsRes?.data || reviewsRes || [];
      setRecentReviews(Array.isArray(reviews) ? reviews : []);

      // Délai d'envoi SMS : on lit la valeur persistée. Default 60 (= 1h après le repas)
      // si la colonne est null ou si le GET /config a échoué.
      if (configRes && Number.isFinite(configRes.delai_envoi_minutes)) {
        setDelaiEnvoiMinutes(configRes.delai_envoi_minutes);
      }

      // Feedbacks privés (funnel V2). Backend wraps dans { feedbacks: [...] }.
      const feedbacks = feedbacksRes?.feedbacks || [];
      setFeedbacksPrives(Array.isArray(feedbacks) ? feedbacks : []);

      // Taux de complétion : avis générés / scans (même calcul que TabQRCode).
      const dStats = dashRes?.stats || {};
      const scansT = dStats.scans_total ?? 0;
      const avisGen = dashRes?.avis_generes_revyo ?? (dStats.satisfaits ?? 0);
      setCompletion({
        scans: scansT,
        avisGeneres: avisGen,
        taux: scansT > 0 ? Math.round((avisGen / scansT) * 100) : 0,
      });
    } catch (e) {
      console.error('[TabCollecte] load error:', e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /** Envoie un SMS de demande d'avis via Brevo (gestion quota côté backend). */
  const handleSubmit = useCallback(async (payload) => {
    try {
      const result = await apiPost('/api/reviews/collect', {
        prenom:         payload.prenom,
        telephone:      payload.tel,
        service:        payload.service,
        delai_minutes:  parseInt(payload.delai, 10) || 0,
        message:        payload.message,
      });
      await loadData(); // refetch quota + historique
      return { success: true, ...result };
    } catch (e) {
      alert(`Erreur envoi SMS : ${e.message}`);
      return { success: false, error: e.message };
    }
  }, [loadData]);

  /**
   * Sprint 1 — ticket 2 : sauvegarde du délai d'envoi SMS (config persistée).
   * Update optimiste : on met à jour l'UI immédiatement, on rollback sur erreur
   * (l'encart DelaiSmsBlock affichera un toast d'erreur via le throw).
   *
   * Validation backend : entier 0..2880 dans CONFIG_FIELDS_SCHEMA + validateConfigField.
   * Hors range → 400, on relance pour que le toast d'erreur s'affiche.
   */
  const handleUpdateDelaiEnvoi = useCallback(async (minutes) => {
    const previous = delaiEnvoiMinutes;
    setDelaiEnvoiMinutes(minutes); // optimiste
    try {
      await apiPut('/api/restaurants/config', { delai_envoi_minutes: minutes });
    } catch (e) {
      setDelaiEnvoiMinutes(previous); // rollback
      throw e;
    }
  }, [delaiEnvoiMinutes]);

  /** Export CSV des contacts envoyés (génération côté frontend, pas besoin du backend). */
  const handleExportCSV = useCallback(() => {
    const header = 'Prénom,Téléphone,Service,Date,Statut\n';
    const rows = contacts.map((c) =>
      [
        c.prenom || '',
        c.telephone || '',
        c.service || '',
        c.created_at || '',
        c.status || '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revyo-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [contacts]);

  return (
    <TabCollecte
      contacts={contacts}
      recentReviews={recentReviews}
      feedbacksPrives={feedbacksPrives}
      smsQuota={smsQuota}
      completion={completion}
      delaiEnvoiMinutes={delaiEnvoiMinutes}
      onSubmit={handleSubmit}
      onExportCSV={handleExportCSV}
      onUpdateDelaiEnvoi={handleUpdateDelaiEnvoi}
    />
  );
}
